import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Calendar as CalIcon, Clock, MapPin, Users } from "lucide-react";
import { CLASSES, getClassBySlug, CONTACT } from "@/data/classes";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import WaitlistDialog from "@/components/WaitlistDialog";

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function Schedule() {
  const [params, setParams] = useSearchParams();
  const activeSlug = params.get("class") || "all";
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waitlistSession, setWaitlistSession] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const query = activeSlug !== "all" ? { class_slug: activeSlug } : {};
        const { data } = await api.get("/sessions", { params: query });
        if (mounted) setSessions(data);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [activeSlug]);

  const grouped = useMemo(() => {
    return sessions.reduce((acc, s) => {
      (acc[s.class_slug] ||= []).push(s);
      return acc;
    }, {});
  }, [sessions]);

  const filterClass = (slug) => {
    if (slug === "all") setParams({});
    else setParams({ class: slug });
  };

  return (
    <div>
      <Seo
        title="Class Schedule"
        path="/schedule"
        description="View upcoming BLS, ACLS, PALS, Heartsaver, and workplace safety class dates. Register in minutes and receive instant confirmation."
      />
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <div className="font-mono-label text-[#d90429] mb-4">Availability</div>
          <h1 className="font-display text-5xl md:text-6xl font-black text-[#0a0a0a] leading-none">
            Upcoming class dates.
          </h1>
          <p className="mt-6 text-lg text-gray-700 max-w-2xl">
            Pick a date, register in minutes, and get instant on-screen confirmation.
            Don&apos;t see a date that works?{" "}
            <a href={CONTACT.phoneHref} className="text-[#d90429] font-bold underline">
              Call {CONTACT.phone}
            </a>
            .
          </p>

          <div className="mt-10 flex flex-wrap gap-2" data-testid="schedule-class-filter">
            {[{ slug: "all", code: "ALL", name: "All Classes" }, ...CLASSES].map((c) => (
              <button
                key={c.slug}
                onClick={() => filterClass(c.slug)}
                data-testid={`filter-${c.slug}`}
                className={`px-4 py-2 rounded-md border text-sm font-bold transition-colors ${
                  activeSlug === c.slug
                    ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                    : "bg-white text-[#0a0a0a] border-gray-300 hover:border-[#0a0a0a]"
                }`}
              >
                {c.code}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#f9fafb] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="text-center py-24 text-gray-500" data-testid="schedule-loading">
              Loading availability…
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-16 bg-white border border-gray-200 rounded-lg" data-testid="schedule-empty">
              <CalIcon className="w-10 h-10 text-gray-400 mx-auto" />
              <h2 className="mt-4 font-display text-2xl font-bold text-[#0a0a0a]">
                No sessions posted yet
              </h2>
              <p className="mt-3 text-gray-600 max-w-md mx-auto">
                We&apos;re updating the calendar. Call{" "}
                <a href={CONTACT.phoneHref} className="text-[#d90429] font-bold">
                  {CONTACT.phone}
                </a>{" "}
                to reserve or request a private session.
              </p>
              <Link
                to="/contact"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#d90429] px-6 py-3 text-sm font-bold text-white hover:bg-black transition-colors"
                data-testid="schedule-empty-contact"
              >
                Request a Date
              </Link>
            </div>
          ) : (
            <div className="space-y-12">
              {Object.entries(grouped).map(([slug, list]) => {
                const c = getClassBySlug(slug);
                if (!c) return null;
                return (
                  <div key={slug} data-testid={`schedule-group-${slug}`}>
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                      <div>
                        <div className="font-mono-label text-[#d90429] mb-1">{c.code}</div>
                        <h2 className="font-display text-3xl md:text-4xl font-black text-[#0a0a0a]">
                          {c.name}
                        </h2>
                      </div>
                      <Link
                        to={`/classes/${c.slug}`}
                        className="text-sm font-bold text-[#0a0a0a] hover:text-[#d90429]"
                      >
                        Class details →
                      </Link>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {list.map((s) => (
                        <div
                          key={s.id}
                          data-testid={`session-card-${s.id}`}
                          className="bg-white border border-gray-200 rounded-lg p-6 hover-lift flex flex-col"
                        >
                          <div className="font-display text-2xl font-black text-[#0a0a0a]">
                            {formatDate(s.date)}
                          </div>
                          <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                            <Clock className="w-4 h-4 text-[#d90429]" />
                            {s.start_time} – {s.end_time}
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-4 h-4 text-[#d90429]" />
                            {s.location}
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                            <Users className="w-4 h-4 text-[#d90429]" />
                            {s.seats_available} of {s.capacity} seats available
                          </div>
                          {s.price > 0 && (
                            <div className="mt-3 font-display text-lg font-bold text-[#0a0a0a]">
                              ${s.price}
                            </div>
                          )}
                          <div className="mt-auto pt-6">
                            {s.seats_available > 0 ? (
                              <Link
                                to={`/register/${s.id}`}
                                data-testid={`session-register-btn-${s.id}`}
                                className="w-full inline-flex justify-center items-center gap-2 rounded-md bg-[#d90429] px-5 py-3 text-sm font-bold text-white hover:bg-black transition-colors"
                              >
                                Register <ArrowRight className="w-4 h-4" />
                              </Link>
                            ) : (
                              <button
                                onClick={() => setWaitlistSession(s)}
                                data-testid={`session-waitlist-btn-${s.id}`}
                                className="w-full inline-flex justify-center items-center gap-2 rounded-md border-2 border-[#0a0a0a] px-5 py-3 text-sm font-bold text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
                              >
                                Session Full — Join Waitlist
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <WaitlistDialog
        session={waitlistSession}
        onClose={() => setWaitlistSession(null)}
      />
    </div>
  );
}
